import { Button } from "@l30/ui";

import { confirmToolLabel } from "./RecordDecisionAction.js";

function portalPreviewFromArgs(args: Record<string, unknown>): {
  engagementName: string;
  objectKind: string;
  objectTitle: string;
  audience: string;
  destination: string;
  sideEffects: string[];
  warnings: string[];
} | null {
  const raw = args["portalPreview"];
  if (!raw || typeof raw !== "object") return null;
  const preview = raw as Record<string, unknown>;
  if (typeof preview["engagementName"] !== "string" || typeof preview["objectTitle"] !== "string") {
    return null;
  }
  return {
    engagementName: preview["engagementName"],
    objectKind: typeof preview["objectKind"] === "string" ? preview["objectKind"] : "item",
    objectTitle: preview["objectTitle"],
    audience: typeof preview["audience"] === "string" ? preview["audience"] : "",
    destination: typeof preview["destination"] === "string" ? preview["destination"] : "",
    sideEffects: Array.isArray(preview["sideEffects"])
      ? preview["sideEffects"].filter((item): item is string => typeof item === "string")
      : [],
    warnings: Array.isArray(preview["warnings"])
      ? preview["warnings"].filter((item): item is string => typeof item === "string")
      : [],
  };
}

export interface AiPendingConfirm {
  toolId: string;
  args: Record<string, unknown>;
  confirmationToken: string;
}

interface AiConfirmBannerProps {
  pending: AiPendingConfirm[];
  confirming: boolean;
  onApprove: (confirmation: AiPendingConfirm) => void;
  onDismiss: () => void;
}

export function AiConfirmBanner({
  pending,
  confirming,
  onApprove,
  onDismiss,
}: AiConfirmBannerProps) {
  if (pending.length === 0) return null;
  return (
    <div className="space-y-2 rounded-soft-md border border-border bg-surface-muted px-3 py-2">
      <p className="text-xs font-medium text-fg">Confirm before continuing</p>
      {pending.map((p, i) => {
        const label = confirmToolLabel(p.toolId, p.args);
        const preview = portalPreviewFromArgs(p.args);
        return (
          <div key={`${p.toolId}-${i}`} className="space-y-2 text-xs">
            <p className="font-medium text-fg">{label ?? p.toolId.replace(/_/g, " ")}</p>
            {preview ? (
              <dl className="grid grid-cols-[7rem_1fr] gap-x-2 gap-y-1 text-fg-muted">
                <dt>Engagement</dt>
                <dd className="text-fg">{preview.engagementName}</dd>
                <dt>Object</dt>
                <dd className="text-fg">
                  {preview.objectTitle} ({preview.objectKind})
                </dd>
                <dt>Audience</dt>
                <dd className="text-fg">{preview.audience}</dd>
                <dt>Destination</dt>
                <dd className="text-fg">{preview.destination}</dd>
                {preview.sideEffects.map((effect) => (
                  <dd key={effect} className="col-span-2">
                    {effect}
                  </dd>
                ))}
                {preview.warnings.map((warning) => (
                  <dd key={warning} className="col-span-2 text-warning-solid">
                    {warning}
                  </dd>
                ))}
              </dl>
            ) : null}
            <details className="text-fg-subtle">
              <summary>Support details</summary>
              <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap">
                {JSON.stringify(p.args, null, 2)}
              </pre>
            </details>
            <div className="flex gap-1">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={confirming}
                onClick={() => onApprove(p)}
              >
                Confirm
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
                Dismiss
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
